import { useState, useEffect, createContext, useContext, useRef } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    GoogleAuthProvider,
    signOut,
    onAuthStateChanged,
    updateProfile,
    updatePassword as firebaseUpdatePassword,
    deleteUser,
    reauthenticateWithCredential,
    EmailAuthProvider,
    sendEmailVerification,
    reload
} from 'firebase/auth';
import type { User as FirebaseUser } from 'firebase/auth';
import { auth } from '../config/firebase';
import toast from 'react-hot-toast';

// 用户类型定义
export interface User {
    id: string;
    username: string;
    email: string;
    avatar?: string;
    provider: 'email' | 'google' | 'temporary';
    createdAt: string;
    emailVerified: boolean;
    isTemporary?: boolean;
    temporaryExpireTime?: number;
}

// 认证状态类型
export interface AuthState {
    user: User | null;
    isLoading: boolean;
    isAuthenticated: boolean;
}

// 认证上下文类型
export interface AuthContextType extends AuthState {
    login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
    register: (username: string, email: string, password: string) => Promise<void>;
    loginWithGoogle: (useRedirect?: boolean) => Promise<void>;
    createTemporaryAccount: () => Promise<void>;
    logout: () => Promise<void>;
    updateProfile: (data: Partial<User>) => Promise<void>;
    updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
    deleteAccount: () => Promise<void>;
    sendVerificationEmail: () => Promise<void>;
    reloadUser: () => Promise<void>;
}

// 创建认证上下文
export const AuthContext = createContext<AuthContextType | null>(null);

// 本地存储键名
const REMEMBER_KEY = 'chaincloud_remember';

// 临时账号配置
const TEMP_ACCOUNT_TIMEOUT = 10 * 60 * 1000; // 10分钟
const TEMP_ACCOUNT_PREFIX = 'temp_';

// 将Firebase用户转换为应用用户格式
const mapFirebaseUserToUser = (firebaseUser: FirebaseUser): User => {
    const provider = firebaseUser.providerData[ 0 ]?.providerId === 'google.com' ? 'google' : 'email';

    return {
        id: firebaseUser.uid,
        username: firebaseUser.displayName || firebaseUser.email?.split('@')[ 0 ] || 'User',
        email: firebaseUser.email || '',
        avatar: firebaseUser.photoURL || undefined,
        provider,
        createdAt: firebaseUser.metadata.creationTime || new Date().toISOString(),
        emailVerified: firebaseUser.emailVerified,
    };
};

// 认证Hook
export const useAuth = (): AuthContextType => {
    const [ authState, setAuthState ] = useState<AuthState>({
        user: null,
        isLoading: true,
        isAuthenticated: false,
    });

    // 用于跟踪用户活动的计时器
    const inactivityTimer = useRef<NodeJS.Timeout | null>(null);
    const lastActivityTime = useRef<number>(Date.now());

    // 重置不活动计时器
    const resetInactivityTimer = () => {
        if (inactivityTimer.current) {
            clearTimeout(inactivityTimer.current);
        }
        lastActivityTime.current = Date.now();

        // 如果是临时账号，设置新的计时器
        if (authState.user?.isTemporary) {
            inactivityTimer.current = setTimeout(() => {
                if (Date.now() - lastActivityTime.current >= TEMP_ACCOUNT_TIMEOUT) {
                    logout();
                }
            }, TEMP_ACCOUNT_TIMEOUT);
        }
    };

    // 监听用户活动
    useEffect(() => {
        const handleActivity = () => {
            resetInactivityTimer();
        };

        // 添加事件监听器
        window.addEventListener('mousemove', handleActivity);
        window.addEventListener('keydown', handleActivity);
        window.addEventListener('click', handleActivity);
        window.addEventListener('scroll', handleActivity);
        window.addEventListener('touchstart', handleActivity);

        return () => {
            // 清理事件监听器
            window.removeEventListener('mousemove', handleActivity);
            window.removeEventListener('keydown', handleActivity);
            window.removeEventListener('click', handleActivity);
            window.removeEventListener('scroll', handleActivity);
            window.removeEventListener('touchstart', handleActivity);
            
            if (inactivityTimer.current) {
                clearTimeout(inactivityTimer.current);
            }
        };
    }, [authState.user?.isTemporary]);

    // 监听认证状态变化
    useEffect(() => {
        // 检查重定向结果
        const checkRedirectResult = async () => {
            try {
                const result = await getRedirectResult(auth);
                if (result) {
                    // 获取Google访问令牌
                    const credential = GoogleAuthProvider.credentialFromResult(result);
                    const token = credential?.accessToken;

                    if (token) {
                        localStorage.setItem('google_access_token', token);
                    }

                    toast.success('Google登录成功！');
                }
            } catch (error: any) {
                console.error('Google重定向登录失败:', error);
                toast.error('Google登录失败');
            }
        };

        checkRedirectResult();

        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            if (firebaseUser) {
                const user = mapFirebaseUserToUser(firebaseUser);

                setAuthState({
                    user,
                    isLoading: false,
                    isAuthenticated: true,
                });
            } else {
                setAuthState({
                    user: null,
                    isLoading: false,
                    isAuthenticated: false,
                });
            }
        });

        return () => unsubscribe();
    }, []);

    // 登录函数
    const login = async (email: string, password: string, rememberMe: boolean = false): Promise<void> => {
        setAuthState(prev => ({ ...prev, isLoading: true }));

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = mapFirebaseUserToUser(userCredential.user);

            // 保存记住我选项
            if (rememberMe) {
                localStorage.setItem(REMEMBER_KEY, 'true');
            } else {
                localStorage.removeItem(REMEMBER_KEY);
            }

            setAuthState({
                user,
                isLoading: false,
                isAuthenticated: true,
            });

            toast.success('登录成功！');
        } catch (error: any) {
            setAuthState(prev => ({ ...prev, isLoading: false }));

            // 处理Firebase错误消息
            let errorMessage = '登录失败';
            switch (error.code) {
                case 'auth/user-not-found':
                    errorMessage = '用户不存在';
                    break;
                case 'auth/wrong-password':
                    errorMessage = '密码错误';
                    break;
                case 'auth/invalid-email':
                    errorMessage = '邮箱格式无效';
                    break;
                case 'auth/user-disabled':
                    errorMessage = '账户已被禁用';
                    break;
                case 'auth/too-many-requests':
                    errorMessage = '请求过于频繁，请稍后再试';
                    break;
                case 'auth/invalid-credential':
                    errorMessage = '邮箱或密码错误';
                    break;
                default:
                    errorMessage = error.message || '登录失败';
            }

            toast.error(errorMessage);
            throw new Error(errorMessage);
        }
    };

    // 注册函数
    const register = async (username: string, email: string, password: string): Promise<void> => {
        setAuthState(prev => ({ ...prev, isLoading: true }));

        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);

            // 更新用户显示名称
            await updateProfile(userCredential.user, {
                displayName: username,
            });

            // 发送邮箱验证邮件
            await sendEmailVerification(userCredential.user);

            const user = mapFirebaseUserToUser(userCredential.user);

            setAuthState({
                user,
                isLoading: false,
                isAuthenticated: true,
            });

            toast.success('注册成功！请检查邮箱验证邮件');
        } catch (error: any) {
            setAuthState(prev => ({ ...prev, isLoading: false }));

            // 处理Firebase错误消息
            let errorMessage = '注册失败';
            switch (error.code) {
                case 'auth/email-already-in-use':
                    errorMessage = '邮箱已被使用';
                    break;
                case 'auth/invalid-email':
                    errorMessage = '邮箱格式无效';
                    break;
                case 'auth/weak-password':
                    errorMessage = '密码强度不够';
                    break;
                case 'auth/operation-not-allowed':
                    errorMessage = '邮箱注册未启用';
                    break;
                default:
                    errorMessage = error.message || '注册失败';
            }

            toast.error(errorMessage);
            throw new Error(errorMessage);
        }
    };

    // Google登录函数
    const loginWithGoogle = async (useRedirect: boolean = false): Promise<void> => {
        setAuthState(prev => ({ ...prev, isLoading: true }));

        try {
            const provider = new GoogleAuthProvider();
            provider.addScope('email');
            provider.addScope('profile');

            let result;
            if (useRedirect) {
                await signInWithRedirect(auth, provider);
                return; // 重定向后页面会重新加载
            } else {
                result = await signInWithPopup(auth, provider);
            }

            if (result) {
                // 获取Google访问令牌
                const credential = GoogleAuthProvider.credentialFromResult(result);
                const token = credential?.accessToken;

                if (token) {
                    localStorage.setItem('google_access_token', token);
                }

                const user = mapFirebaseUserToUser(result.user);

                setAuthState({
                    user,
                    isLoading: false,
                    isAuthenticated: true,
                });

                toast.success('Google登录成功！');
            }
        } catch (error: any) {
            setAuthState(prev => ({ ...prev, isLoading: false }));

            let errorMessage = 'Google登录失败';
            switch (error.code) {
                case 'auth/popup-closed-by-user':
                    errorMessage = '登录窗口被关闭';
                    break;
                case 'auth/popup-blocked':
                    errorMessage = '弹窗被阻止，请允许弹窗或使用其他登录方式';
                    break;
                case 'auth/cancelled-popup-request':
                    errorMessage = '登录请求被取消';
                    break;
                case 'auth/account-exists-with-different-credential':
                    errorMessage = '该邮箱已使用其他方式注册';
                    break;
                default:
                    errorMessage = error.message || 'Google登录失败';
            }

            toast.error(errorMessage);
            throw new Error(errorMessage);
        }
    };

    // 创建临时账号
    const createTemporaryAccount = async (): Promise<void> => {
        setAuthState(prev => ({ ...prev, isLoading: true }));

        try {
            // 在开发环境或Mock模式下，创建虚拟临时账号
            if (process.env.NODE_ENV === 'development' || import.meta.env.VITE_MOCK_ENABLED === 'true') {
                const tempUsername = `访客_${Math.random().toString(36).substr(2, 8)}`;
                const tempUser: User = {
                    id: `temp_${Date.now()}`,
                    username: tempUsername,
                    email: `temp_${Date.now()}@temp.chaincloud.com`,
                    provider: 'temporary',
                    createdAt: new Date().toISOString(),
                    emailVerified: false,
                    isTemporary: true,
                    temporaryExpireTime: Date.now() + TEMP_ACCOUNT_TIMEOUT,
                };

                setAuthState({
                    user: tempUser,
                    isLoading: false,
                    isAuthenticated: true,
                });

                // 设置不活动计时器
                resetInactivityTimer();

                toast.success('已创建临时账号，可以免费试用10分钟');
                return;
            }

            // 生产环境下使用Firebase创建真实临时账号
            const tempEmail = `${TEMP_ACCOUNT_PREFIX}${Date.now()}@temp.chaincloud.com`;
            const tempPassword = Math.random().toString(36).slice(-8);
            const tempUsername = `访客_${Date.now().toString(36)}`;

            const userCredential = await createUserWithEmailAndPassword(auth, tempEmail, tempPassword);

            // 更新用户显示名称
            await updateProfile(userCredential.user, {
                displayName: tempUsername,
            });

            const user = {
                ...mapFirebaseUserToUser(userCredential.user),
                isTemporary: true,
                temporaryExpireTime: Date.now() + TEMP_ACCOUNT_TIMEOUT,
                provider: 'temporary' as const,
            };

            setAuthState({
                user,
                isLoading: false,
                isAuthenticated: true,
            });

            // 设置不活动计时器
            resetInactivityTimer();

            toast.success('已创建临时账号，可以免费试用10分钟');
        } catch (error: any) {
            setAuthState(prev => ({ ...prev, isLoading: false }));
            
            let errorMessage = '创建临时账号失败，请稍后重试';
            console.error('临时账号创建失败:', error);
            
            // 处理具体的Firebase错误
            switch (error.code) {
                case 'auth/operation-not-allowed':
                    errorMessage = '临时账号功能暂时不可用';
                    break;
                case 'auth/too-many-requests':
                    errorMessage = '请求过于频繁，请稍后再试';
                    break;
                case 'auth/network-request-failed':
                    errorMessage = '网络连接失败，请检查网络';
                    break;
                default:
                    errorMessage = error.message || '创建临时账号失败，请稍后重试';
            }
            
            toast.error(errorMessage);
            throw new Error(errorMessage);
        }
    };

    // 修改登出函数以处理临时账号
    const logout = async (): Promise<void> => {
        try {
            // 在开发环境或Mock模式下，直接清理状态
            if (process.env.NODE_ENV === 'development' || import.meta.env.VITE_MOCK_ENABLED === 'true') {
                if (authState.user?.isTemporary) {
                    toast.success('临时账号已删除');
                } else if (authState.user) {
                    toast.success('已退出登录');
                }

                setAuthState({
                    user: null,
                    isLoading: false,
                    isAuthenticated: false,
                });

                if (inactivityTimer.current) {
                    clearTimeout(inactivityTimer.current);
                    inactivityTimer.current = null;
                }
                return;
            }

            // 生产环境下的正常登出流程
            if (authState.user?.isTemporary && auth.currentUser) {
                await deleteUser(auth.currentUser);
                toast.success('临时账号已删除');
            }

            await signOut(auth);
            setAuthState({
                user: null,
                isLoading: false,
                isAuthenticated: false,
            });

            if (inactivityTimer.current) {
                clearTimeout(inactivityTimer.current);
                inactivityTimer.current = null;
            }

            if (!authState.user?.isTemporary) {
                toast.success('已退出登录');
            }
        } catch (error: any) {
            console.error('退出登录失败:', error);
            toast.error('退出登录失败');
        }
    };

    // 更新用户资料
    const updateUserProfile = async (data: Partial<User>): Promise<void> => {
        if (!auth.currentUser) {
            throw new Error('用户未登录');
        }

        try {
            const updateData: any = {};
            if (data.username) updateData.displayName = data.username;
            if (data.avatar) updateData.photoURL = data.avatar;

            await updateProfile(auth.currentUser, updateData);
            await reload(auth.currentUser);

            const updatedUser = mapFirebaseUserToUser(auth.currentUser);
            setAuthState(prev => ({ ...prev, user: updatedUser }));

            toast.success('资料更新成功');
        } catch (error: any) {
            toast.error('资料更新失败');
            throw new Error(error.message || '资料更新失败');
        }
    };

    // 更新密码
    const updateUserPassword = async (currentPassword: string, newPassword: string): Promise<void> => {
        if (!auth.currentUser || !auth.currentUser.email) {
            throw new Error('用户未登录');
        }

        try {
            // 重新认证
            const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
            await reauthenticateWithCredential(auth.currentUser, credential);

            // 更新密码
            await firebaseUpdatePassword(auth.currentUser, newPassword);

            toast.success('密码更新成功');
        } catch (error: any) {
            let errorMessage = '密码更新失败';
            switch (error.code) {
                case 'auth/wrong-password':
                    errorMessage = '当前密码错误';
                    break;
                case 'auth/weak-password':
                    errorMessage = '新密码强度不够';
                    break;
                case 'auth/requires-recent-login':
                    errorMessage = '请重新登录后再试';
                    break;
                default:
                    errorMessage = error.message || '密码更新失败';
            }

            toast.error(errorMessage);
            throw new Error(errorMessage);
        }
    };

    // 删除账户
    const deleteUserAccount = async (): Promise<void> => {
        if (!auth.currentUser) {
            throw new Error('用户未登录');
        }

        try {
            await deleteUser(auth.currentUser);

            setAuthState({
                user: null,
                isLoading: false,
                isAuthenticated: false,
            });

            toast.success('账户已删除');
        } catch (error: any) {
            let errorMessage = '删除账户失败';
            if (error.code === 'auth/requires-recent-login') {
                errorMessage = '请重新登录后再试';
            }

            toast.error(errorMessage);
            throw new Error(errorMessage);
        }
    };

    // 发送验证邮件
    const sendVerificationEmail = async (): Promise<void> => {
        if (!auth.currentUser) {
            throw new Error('用户未登录');
        }

        try {
            await sendEmailVerification(auth.currentUser);
            toast.success('验证邮件已发送');
        } catch (error: any) {
            toast.error('发送验证邮件失败');
            throw new Error(error.message || '发送验证邮件失败');
        }
    };

    // 重新加载用户信息
    const reloadUser = async (): Promise<void> => {
        if (!auth.currentUser) {
            throw new Error('用户未登录');
        }

        try {
            await reload(auth.currentUser);
            const updatedUser = mapFirebaseUserToUser(auth.currentUser);
            setAuthState(prev => ({ ...prev, user: updatedUser }));
        } catch (error: any) {
            throw new Error(error.message || '刷新用户信息失败');
        }
    };

    return {
        ...authState,
        login,
        register,
        loginWithGoogle,
        createTemporaryAccount,
        logout,
        updateProfile: updateUserProfile,
        updatePassword: updateUserPassword,
        deleteAccount: deleteUserAccount,
        sendVerificationEmail,
        reloadUser,
    };
};

// 使用认证上下文的Hook
export const useAuthContext = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuthContext必须在AuthProvider内使用');
    }
    return context;
}; 